"use client";
import { createContext, useContext, type ReactNode } from "react";
import type { Post } from "../types";

export type AttrPath = (string | number)[];

/**
 * Block context. `TData` types the server-resolved `data` of the block (the
 * return value of its server loader); declare it at the call site with
 * `useBlock<{ posts: Post[] }>()`. The default keeps keyed access legal.
 */
export interface BlockCtx<TData = Record<string, unknown>> {
  themeId?: string;
  areaDepth?: number;
  preview?: boolean;
  /** Rendering a block chooser sample: empty attributes show placeholders (preview-placeholders.ts). */
  sample?: boolean;
  attrs: Record<string, unknown>;
  /**
   * Server-resolved data for this block (posts, form def, ...). `undefined`
   * when the block has no loader (or the builder hasn't fetched it yet) and
   * `null` when the loader failed, so read it with `data?.key`.
   */
  data: TData | null | undefined;
  locale: string;
  locales: string[];
  localeDomains?: Record<string, string>;
  localeAlternates?: Record<string, string>;
  enabledModules: string[];
  editing: boolean;
  blockIndex: number;
  setAttr: (path: AttrPath, value: unknown) => void;
  /** Current post, including unsaved form edits, when rendering a post builder/detail. */
  post?: Post;
  /** Prefix for nested scopes (repeater items). */
  scope: AttrPath;
}

const Ctx = createContext<BlockCtx<unknown> | null>(null);

export function BlockProvider(props: { value: BlockCtx<unknown>; children: ReactNode }) {
  return <Ctx.Provider value={props.value}>{props.children}</Ctx.Provider>;
}

/**
 * Current block context. Pass the block's data shape to type `data`:
 * `const { data } = useBlock<{ searchProducts?: Product[] }>()` and then
 * `data?.searchProducts`. The type argument is a compile-time declaration
 * only; the data itself is whatever the block's server loader returned.
 */
export function useBlock<TData = Record<string, unknown>>(): BlockCtx<TData> {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error("Attribute components must be used inside a block");
  return ctx as BlockCtx<TData>;
}

export function getIn(obj: unknown, path: AttrPath): unknown {
  let cur: unknown = obj;
  for (const key of path) {
    if (cur == null || typeof cur !== "object") return undefined;
    cur = (cur as Record<string | number, unknown>)[key];
  }
  return cur;
}

export function setIn(obj: unknown, path: AttrPath, value: unknown): unknown {
  if (!path.length) return value;
  const [head, ...rest] = path;
  const base: Record<string | number, unknown> | unknown[] =
    typeof head === "number"
      ? Array.isArray(obj) ? [...obj] : []
      : obj && typeof obj === "object" && !Array.isArray(obj) ? { ...(obj as object) } : {};
  (base as Record<string | number, unknown>)[head] = setIn(
    (base as Record<string | number, unknown>)[head],
    rest,
    value
  );
  return base;
}

/** Read/write helpers bound to the current scope. */
export function useAttr<T = unknown>(name: string): [T | undefined, (v: T) => void] {
  const ctx = useBlock();
  const path = [...ctx.scope, name];
  const value = getIn(ctx.attrs, path) as T | undefined;
  return [value, (v: T) => ctx.setAttr(path, v)];
}
